import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Save, MapPin, Video } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const DAYS = [
  { k: "0", name: "Mon" },
  { k: "1", name: "Tue" },
  { k: "2", name: "Wed" },
  { k: "3", name: "Thu" },
  { k: "4", name: "Fri" },
  { k: "5", name: "Sat" },
  { k: "6", name: "Sun" },
];

const EMPTY = {
  slug: "",
  name: "",
  type: "in-person",
  address: "",
  description: "",
  schedule: {},
};

export default function LocationsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const save = async (payload, isNew) => {
    try {
      if (isNew) {
        await api.post("/admin/locations", payload);
        toast.success("Location created");
      } else {
        await api.patch(`/admin/locations/${editing.id}`, payload);
        toast.success("Location updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/locations/${loc.id}`);
      toast.success("Location deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="locations-manager">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif text-2xl text-ink">Locations</div>
          <div className="text-sm text-ink-soft">Add in-person or online locations and set weekly schedules.</div>
        </div>
        <button
          onClick={() => setEditing(EMPTY)}
          data-testid="add-location-button"
          className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-medium hover:bg-navy-deep transition-colors"
        >
          <Plus size={14} /> New location
        </button>
      </div>

      {loading && (
        <div className="py-12 flex items-center justify-center text-ink-muted">
          <Loader2 size={18} className="animate-spin mr-2" /> Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-14 text-ink-soft italic font-serif text-lg bg-cream-soft border border-sand/60 rounded-3xl">
          No locations yet. Click "New location" to add one.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((loc) => (
            <div key={loc.id} className="bg-cream-soft border border-sand/70 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-navy mb-2">
                {loc.type === "online" ? <Video size={13} /> : <MapPin size={13} />}
                {loc.type === "online" ? "Telehealth" : "In person"}
              </div>
              <div className="font-serif text-xl text-ink mb-1">{loc.name}</div>
              <div className="text-xs text-ink-soft mb-3 lowercase font-mono">{loc.slug}</div>
              <div className="text-sm text-ink-muted line-clamp-2 mb-3">{loc.description}</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {DAYS.map((d) => {
                  const range = loc.schedule?.[d.k];
                  const open = !!range;
                  return (
                    <div
                      key={d.k}
                      className={`px-2 py-1 rounded text-[11px] ${
                        open ? "bg-navy text-cream" : "bg-cream border border-sand text-ink-soft/60"
                      }`}
                      title={open ? `${range[0]}:00 – ${range[1]}:00` : "Closed"}
                    >
                      {d.name}
                      {open && <span className="ml-1 opacity-80">{range[0]}–{range[1]}</span>}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setEditing(loc)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-navy hover:text-navy"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => remove(loc)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-cream border border-sand px-3 py-1.5 text-xs hover:border-red-300 hover:text-red-700"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <LocationForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function LocationForm({ initial, onSave, onClose }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...EMPTY, ...initial, schedule: { ...(initial.schedule || {}) } });
  const [saving, setSaving] = useState(false);

  const toggleDay = (k) => {
    setForm((f) => {
      const s = { ...f.schedule };
      if (s[k]) delete s[k];
      else s[k] = [9, 17];
      return { ...f, schedule: s };
    });
  };
  const setHours = (k, idx, val) => {
    setForm((f) => {
      const s = { ...f.schedule };
      const cur = s[k] || [9, 17];
      const num = Math.max(0, Math.min(24, Number(val) || 0));
      s[k] = idx === 0 ? [num, cur[1]] : [cur[0], num];
      return { ...f, schedule: s };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(
        {
          slug: form.slug.trim(),
          name: form.name.trim(),
          type: form.type,
          address: form.address.trim(),
          description: form.description.trim(),
          schedule: form.schedule,
        },
        isNew
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-cream rounded-[2rem] p-8 max-w-2xl w-full shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="font-serif text-2xl text-ink">{isNew ? "New location" : "Edit location"}</div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="location-form">
          <Row label="Name">
            <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
          </Row>
          <div className="grid grid-cols-2 gap-4">
            <Row label="Slug" hint="url id (lowercase, dashes)">
              <input className={inp} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required pattern="[a-z0-9-]+" />
            </Row>
            <Row label="Type">
              <select className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="in-person">In person</option>
                <option value="online">Online / Telehealth</option>
              </select>
            </Row>
          </div>
          <Row label="Address / Meeting info">
            <input className={inp} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Row>
          <Row label="Description">
            <textarea rows={3} className={`${inp} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Row>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 block">Weekly schedule</label>
            <div className="space-y-2">
              {DAYS.map((d) => {
                const range = form.schedule[d.k];
                const open = !!range;
                return (
                  <div key={d.k} className="flex items-center gap-3 bg-cream-soft rounded-xl p-2.5 border border-sand/60">
                    <label className="flex items-center gap-2 w-24 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={open}
                        onChange={() => toggleDay(d.k)}
                        className="accent-navy h-4 w-4"
                      />
                      {d.name}
                    </label>
                    {open ? (
                      <div className="flex items-center gap-2 flex-1 text-sm">
                        <input
                          type="number"
                          min="0"
                          max="24"
                          value={range[0]}
                          onChange={(e) => setHours(d.k, 0, e.target.value)}
                          className="w-20 bg-cream border border-sand rounded-lg px-2 py-1"
                        />
                        <span className="text-ink-soft">to</span>
                        <input
                          type="number"
                          min="0"
                          max="24"
                          value={range[1]}
                          onChange={(e) => setHours(d.k, 1, e.target.value)}
                          className="w-20 bg-cream border border-sand rounded-lg px-2 py-1"
                        />
                        <span className="text-xs text-ink-soft">(24-hr, slots are 1 hr each)</span>
                      </div>
                    ) : (
                      <div className="text-sm text-ink-soft italic">Closed</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-ink/20 px-5 py-2.5 text-sm hover:border-navy hover:text-navy">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-cream-soft border border-sand/80 rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy";
function Row({ label, hint, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-ink-soft mt-1">{hint}</div>}
    </div>
  );
}
